import { t } from '@/i18n'

function toNum(code) {
  if (code === null || code === undefined || code === '') return null
  const n = Number(code)
  return Number.isNaN(n) ? null : n
}

function enumLabel(namespace, code) {
  const n = toNum(code)
  if (n === null) return '—'
  const key = `enum.${namespace}.${n}`
  const label = t(key)
  if (label !== key) return label
  return t('enum.unknown', { value: code })
}

function buildOptions(namespace, values) {
  return values.map(value => ({
    value,
    label: enumLabel(namespace, value)
  }))
}

/**
 * Use inside Vue `computed` so option labels follow the current locale.
 * Reference `this.$locale` in the same computed so it re-runs after language switch.
 */
export function enumSelectOptions(t, namespace, values) {
  return values.map(value => ({
    value,
    label: t(`enum.${namespace}.${value}`)
  }))
}

export const QUOTE_STATUS = { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 7: 7 }
export const QUOTE_INTERNAL_APPROVAL = { 0: 0, 1: 1, 2: 2, 3: 3 }
export const QUOTE_CUSTOMER_DECISION = { 1: 1, 2: 2, 3: 3 }
export const CONTRACT_STATUS = { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5 }
export const TEMPLATE_STATUS = { 1: 1, 2: 2 }
export const PAYMENT_PLAN_STATUS = { 1: 1, 2: 2, 3: 3 }
export const CHANGE_ORDER_APPROVAL = { 1: 1, 2: 2, 3: 3, 4: 4 }

/** 草稿(1)、已提交(2) 可批准/拒绝；已批准(3)、已拒绝(4) 不可（避免终态仍显示操作） */
export function changeOrderCanApproveReject(approvalStatusCode) {
  const n = toNum(approvalStatusCode)
  if (n === null) return true
  return n === 1 || n === 2
}
export const INVOICE_TYPE = { 1: 1, 2: 2 }
export const PROJECT_STATUS = { 1: 1, 2: 2, 3: 3, 4: 4 }
export const LEAD_STATUS = { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5 }
export const CUSTOMER_STATUS = { 1: 1, 2: 2 }
export const INVOICE_STATUS = { 1: 1, 2: 2 }

export const labelQuoteStatus = code => enumLabel('quoteStatus', code)
export const labelQuoteInternalApproval = code => enumLabel('quoteInternalApproval', code)
export const labelQuoteCustomerDecision = code => enumLabel('quoteCustomerDecision', code)
export const labelContractStatus = code => enumLabel('contractStatus', code)
export const labelTemplateStatus = code => enumLabel('templateStatus', code)
export const labelPaymentPlanStatus = code => enumLabel('paymentPlanStatus', code)
export const labelChangeOrderApproval = code => enumLabel('changeOrderApproval', code)
export const labelInvoiceType = code => enumLabel('invoiceType', code)
export const labelProjectStatus = code => enumLabel('projectStatus', code)
export const labelLeadStatus = code => enumLabel('leadStatus', code)
export const labelCustomerStatus = code => enumLabel('customerStatus', code)
export const labelInvoiceStatus = code => enumLabel('invoiceStatus', code)
export const labelFollowupType = code => enumLabel('followupType', code)

export const PAYMENT_PLAN_STATUS_OPTIONS = buildOptions('paymentPlanStatus', [1, 2, 3])
export const INVOICE_TYPE_OPTIONS = buildOptions('invoiceType', [1, 2])
export const LEAD_STATUS_OPTIONS = buildOptions('leadStatus', [1, 2, 4, 5])
export const CUSTOMER_STATUS_OPTIONS = buildOptions('customerStatus', [1, 2])
export const FOLLOWUP_TYPE_OPTIONS = buildOptions('followupType', [1, 2, 3, 5, 4])

export function labelEnum(namespace, code) {
  return enumLabel(namespace, code)
}

export function labelQuotePrimary(row) {
  if (!row) return '—'
  const qs = toNum(row.quoteStatusCode)
  const ic = toNum(row.internalApprovalCode)
  const cd = toNum(row.customerDecisionCode)
  if (qs === 5 || cd === 2) return t('enum.quoteStatus.5')
  if (qs === 6 || cd === 3) return t('enum.quoteStatus.6')
  if (qs === 7) return t('enum.quoteStatus.7')
  if (qs === 4) return t('enum.quoteStatus.4')
  // 与报价列表「报价状态」筛选同一套 enum.quoteStatus 文案（避免内部通过却显示「已通过」）
  if (qs === 2) return t('enum.quoteStatus.2')
  if (qs === 3) return t('enum.quoteStatus.3')
  if (ic === 1) return t('enum.quoteInternalApproval.1')
  if (ic === 3) return t('enum.quoteInternalApproval.3')
  if (ic === 2) return t('enum.quoteStatus.2')
  if (ic === 0 || ic === null) return t('enum.quoteStatus.1')
  return labelQuoteStatus(qs)
}

/** 客户公开页状态文案（不展示「客户已查看」，避免客户看到自己被标记） */
export function labelPublicQuoteStatus(row) {
  if (!row) return '—'
  const qs = toNum(row.quoteStatusCode)
  const cd = toNum(row.customerDecisionCode)
  if (qs === 5 || cd === 2) return t('enum.quoteStatus.5')
  if (qs === 6 || cd === 3) return t('enum.quoteStatus.6')
  if (qs === 7) return t('publicQuote.statusChipViewed')
  if (qs === 4) return t('enum.quoteStatus.4')
  if (qs === 2) return t('enum.quoteStatus.2')
  if (qs === 3) return t('enum.quoteStatus.3')
  const ic = toNum(row.internalApprovalCode)
  if (ic === 1) return t('enum.quoteInternalApproval.1')
  if (ic === 3) return t('enum.quoteInternalApproval.3')
  if (ic === 2) return t('enum.quoteStatus.2')
  if (ic === 0 || ic === null) return t('enum.quoteStatus.1')
  return labelQuoteStatus(qs)
}

export function quoteUiFlags(row) {
  const qs = toNum(row.quoteStatusCode)
  const ic = toNum(row.internalApprovalCode)
  const decided = qs === 5 || qs === 6
  const editable = !decided && (ic === null || ic === 0 || ic === 3)
  const canSubmit = !decided && (ic === 0 || ic === 3)
  const canApprove = ic === 1
  const canSend = ic === 2 && !decided
  return { editable, canSubmit, canApprove, canSend }
}

export function contractStatusTagType(code) {
  const n = toNum(code)
  const map = { 1: 'info', 2: 'warning', 3: 'success', 4: '', 5: 'danger' }
  return map[n] != null ? map[n] : 'info'
}
